import { IconFont } from '@byecode/ui'
import React, { useCallback } from 'react'
import type { Accept } from 'react-dropzone'
import styled from 'styled-components'

import type { MdFileEntry } from '../../utils/markdown'
import UploadArea from '../UploadArea'

interface UploadStepProps {
    entries: MdFileEntry[]
    onAddFile: (file: File) => Promise<void> | void
    onRemove: (index: number) => void
}

const MARKDOWN_ACCEPT: Accept = {
    'text/markdown': ['.md'],
    'application/zip': ['.zip'],
    'application/x-zip-compressed': ['.zip']
}
const TipWrapper = styled.div`
    padding: 8px 0;
`

const Tip = styled.div`
    position: relative;
    font-size: var(--font-size-normal);
    color: var(--color-gray-400);
    padding: 4px 10px;

    &:before {
        content: '';
        position: absolute;
        left: 0;
        top: 10px;
        width: 4px;
        height: 4px;
        border-radius: 4px;
        background-color: var(--color-gray-400);
    }
`

const FileList = styled.div`
    margin-top: 12px;
    display: flex;
    flex-direction: column;
    gap: 8px;
`

const FileRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 12px;
    border: 1px solid var(--color-gray-200);
    border-radius: 6px;
`

const FileName = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: var(--font-size-normal);
    color: var(--color-gray-600);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`

const FileSize = styled.span`
    color: var(--color-gray-400);
    font-size: var(--font-size-sm);
    flex-shrink: 0;
`

const RemoveIcon = styled(IconFont)`
    cursor: pointer;
    color: var(--color-gray-400);
    margin-left: 12px;
    flex-shrink: 0;

    &:hover {
        color: var(--color-red-500);
    }
`

const formatSize = (size: number): string => {
    if (size >= 1024 * 1024) {
        return `${(size / 1024 / 1024).toFixed(1)} MB`
    }
    return `${Math.max(1, Math.round(size / 1024))} KB`
}

export const UploadStep: React.FC<UploadStepProps> = ({ entries, onAddFile, onRemove }) => {
    const handleFileLoaded = useCallback(
        (file: File) => {
            return onAddFile(file)
        },
        [onAddFile]
    )

    return (
        <div>
            <UploadArea
                file={null}
                accept={MARKDOWN_ACCEPT}
                sizeLimit={100}
                description="点击或拖拽上传 .md / .zip 文件"
                rejectMessage="仅支持上传 .md、.zip 格式的文件"
                onFileLoaded={handleFileLoaded}
            />
            <TipWrapper>
                <Tip>文件大小不能超过 100 MB</Tip>
                <Tip>仅支持 .md、.zip 格式的文件</Tip>
                <Tip>如上传 zip 压缩包，请将 md 文件放于 1 个文件夹（请勿创建子文件夹）后压缩为 zip 上传</Tip>
            </TipWrapper>
            {entries.length > 0 && (
                <FileList>
                    {entries.map((entry, index) => (
                        <FileRow key={entry.fileName}>
                            <FileName>
                                <IconFont type="FileText" style={{ color: 'var(--color-gray-400)' }} />
                                <span>{entry.fileName}</span>
                                <FileSize>{formatSize(entry.size)}</FileSize>
                            </FileName>
                            <RemoveIcon type="Trash" size={16} onClick={() => onRemove(index)} />
                        </FileRow>
                    ))}
                </FileList>
            )}
        </div>
    )
}
